/**
 * Hook do histórico Realizado × DECOMP × Δ (Task #29 PR3 — consome
 * /api/v2/pld/historico-decomp). Keyed por submercado (a DECOMP é por submercado;
 * S/NE/N podem não ter série → decomp/delta None). Cache module-level, TTL 5 min.
 */
import { useEffect, useState } from "react";
import { getPldHistoricoDecomp } from "../api/client";
import type { PldHistoricoDecompResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldHistoricoDecompResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldHistoricoDecompCache(): void {
  cache.clear();
}

export interface UsePldHistoricoDecompState {
  data: PldHistoricoDecompResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldHistoricoDecomp(submercado: string): UsePldHistoricoDecompState {
  const [state, setState] = useState<UsePldHistoricoDecompState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = submercado;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getPldHistoricoDecomp(submercado),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [submercado]);

  return state;
}
